import CoopPriceProjectionInterface from "@app/Interface/CoopPriceProjectionInterface";
import MontanaPriceProjectionInterface from "@app/Interface/MontanaPriceProjectionInterface";
import { FedexIcon } from "@patternfly/react-icons";
import TypeAssertions from "./TypeAssertions";

/**
 * Formatters changes the display of a cell but
 * doesn't change the value stored in the cell.
 * see: http://tabulator.info/docs/4.9/format#format-custom
 */
export default class TabulatorFormatters {

    /**
     * Allowed formatterParams: 
     *   threshold: number
     *   compareType: "greater" | "greaterOrEqual" | "lesser" | "lesserOrEqual". Defaults to "greater"
     * 
     * @param cell            The cell component object
     * @param formatterParams parameters set for the column
     * @param onRendered      function to call when the formatter is rendered. 
     * @returns 
     */
    static recentResultsFormatter = (cell: any, formatterParams: {threshold: number, compareType: string, decimalPlaces?: number}, onRendered: () => void): string => {

        const value = cell.getValue();

        const valueNumber = Number(value);

        let formattedValue = `${value} (err!)`;

        const decimalPlaces = formatterParams.decimalPlaces ?? 0;

        // for reference
        const fieldName = cell.getColumn().getField();

        // check assertions
        if (TypeAssertions.isStringNullOrEmpty(value)) {

            // no value, display a dash
            formattedValue = "&mdash;";

        } else if (value === "#") {

            formattedValue = "#";

            const cellObject: HTMLElement = cell.getElement();

            cellObject.className = cellObject.className + " pf-u-danger-color-100";

        } else if (Number.isNaN(valueNumber) === false && formatterParams.threshold) {

            formattedValue = valueNumber.toLocaleString("en", {maximumFractionDigits: decimalPlaces, minimumFractionDigits: decimalPlaces});

            let compareType = String(formatterParams.compareType);

            const validTypes = ["greater", "greaterOrEqual", "lesser", "lesserOrEqual"];

            if (TypeAssertions.isStringNullOrEmpty(compareType) || validTypes.indexOf(compareType) === -1) {
                compareType = "greater";
            }

            // if threshold exists, check for warning
            const threshold = Number(formatterParams.threshold);

            const compareValue = Number(value);

            let isDanger = false;

            if (compareType === "greater" && compareValue > threshold) {

                isDanger = true;

            } else if (compareType === "greaterOrEqual" && compareValue >= threshold) {

                isDanger = true;

            } else if (compareType === "lesser" && compareValue < threshold) {

                isDanger = true;

            } else if (compareType === "lesserOrEqual" && compareValue <= threshold) {

                isDanger = true;
            }

            if (isDanger) {

                const cellObject: HTMLElement = cell.getElement();

                cellObject.className = cellObject.className + " pf-u-danger-color-100";
            }

        } else if (Number.isNaN(valueNumber) === false) {

            formattedValue = valueNumber.toLocaleString("en-US", {maximumFractionDigits: decimalPlaces, minimumFractionDigits: decimalPlaces});
            
        }

        return formattedValue;

    }

    /**
     * Special Formatter for the AB_Test column 
     *   threshold: number
     *   compareType: "greater" | "greaterOrEqual" | "lesser" | "lesserOrEqual". Defaults to "greater"
     * 
     * @param cell            The cell component object
     * @param formatterParams parameters set for the column
     * @param onRendered      function to call when the formatter is rendered. 
     * @returns 
     */
    static abTestFormatter = (cell: any, formatterParams: any, onRendered: any): string => {

        const value = cell.getValue();

        let formattedValue: string;

        switch (value) {
            case "N":
                formattedValue = "N-IMS";
                break;
            case "0":
                formattedValue = "N-IMS";
                break;
            case "X":
                formattedValue = "N";
                break;
            default:
                formattedValue = value;
                break;
        }

        return formattedValue;

    }


    /**
     * Function that formats the date value of column
     * see: http://tabulator.info/docs/4.9/mutators
     * 
     * Note: mutator was replaced by tabulator's formatter which requires moment.js and 
     * moment-timezone.js. if we need to reduce size, we could remove moment and 
     * just use this mutator.
     * 
     * @param value original value of the cell
     * @param _data  the data for the row
     * @param _type  the type of mutation occurring  (data|edit)
     * @param _params the mutatorParams object from the column definition
     * @param component when the "type" argument is "edit", this contains the cell component for the edited cell, otherwise it is the column component for the column
     */
    static dateMutator = (cell: any, formatterParams: any, onRendered: any) => {

        const months = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec"
        ];

        const pickupDate = new Date(cell.getValue());
        // Noote: getMonth() is the index value
        const month = pickupDate.getMonth();
        const day = pickupDate.getDay();
        const displayMonth = months[month];
        const formattedDate = `${displayMonth} ${day}`;

        return formattedDate;

    }

    /**
     * Converts a month number to the full month name.
     * 
     * @param cell            The cell component object
     * @param formatterParams parameters set for the column
     * @param onRendered      function to call when the formatter is rendered. 
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    static monthNameMutator = (cell: any, formatterParams: any, onRendered: any): string => {
    
        const value = cell.getValue();

        let monthIndex = Number(value) - 1;

        if (isNaN(monthIndex)) {
            monthIndex = 1;
        }

        const months = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December"
        ];

        const formattedValue = months[monthIndex];

        return formattedValue;

    }

    /**
     * if the row is in the future, change the font color to red.
     * 
     * @param row Tabulator row object
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    static coopRowFormatter = (row: any): void => {

        const data: CoopPriceProjectionInterface = row.getData();

        const currentDate = new Date();

        // we want just the first day of the month with no time. 
        // NOTE: currentDate.getMonth() is the index not the actual month 
        const dateThreshold = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

        const month = Number(data.PricingMonth) ?? 1;

        const year = Number(data.PricingYear) ?? currentDate.getFullYear();

        // NOTE: Date(Year, month index!!!, day); 
        const rowDate = new Date(year, month - 1, 1);

        if (rowDate > dateThreshold) {

            const currentRow: HTMLElement = row.getElement();

            currentRow.className += " pf-u-danger-color-100";
        }

    }

    /**
     * if the row is in the future, change the font color to red.
     * 
     * @param row Tabulator row object
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    static montanaRowFormatter = (row: any): void => {

        const data: MontanaPriceProjectionInterface = row.getData();

        const currentDate = new Date();

        // we want just the first day of the month with no time. 
        // NOTE: currentDate.getMonth() is the index not the actual month 
        const dateThreshold = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

        const month = Number(data.PriceMonth) ?? 1;

        const year = Number(data.PriceYear) ?? currentDate.getFullYear();

        // NOTE: Date(Year, month index!!!, day); 
        const rowDate = new Date(year, month - 1, 1);

        if (rowDate > dateThreshold ) {

            const currentRow: HTMLElement = row.getElement();

            currentRow.className += " pf-u-danger-color-100";
        }

    }

    /**
     * Formats a cell's value as blue text with an underline based on the cell's corresponding
     * flag field value. Also formats the LEUC/SCC field as red if its value is over a given threshold.
     * Also also formats numbers to remove extra  decimal places (i.e. "87.010" -> 87.01)
     * 
     * Allowed formatterParams: 
     *   flagField: string
     * 
     * @see https://bitbucket.org/sitecrafting/nda-solution-original/src/master/MyNDA/Views/Grids/CurrentComponents.ascx.cs
     * @param cell            The cell component object
     * @param formatterParams parameters set for the column
     * @param onRendered      function to call when the formatter is rendered. 
     * @returns 
     */
    static dailyComponentsSubstitutionFormatter = (cell: any, formatterParams: {flagField: string, decimalPlaces?: number}, onRendered: () => void): string => {
        const value = cell.getValue();
        const decimalPlaces = formatterParams.decimalPlaces ?? 0;
        let formattedValue = value === null ? '—' : value;
        formattedValue = formattedValue && formattedValue !== '—' ? Number(formattedValue).toLocaleString("en", {maximumFractionDigits: decimalPlaces, minimumFractionDigits: decimalPlaces}) : formattedValue;
        const flagName = formatterParams.flagField;
        const flagValue = cell.getData()[flagName] ?? '';
        const leucTestThreshold = Number(process.env.THRESHOLD_LEUC_TEST) ?? 350;
        const cellObject: HTMLElement = cell.getElement();

        if (formattedValue !== '—' && flagValue === "#") {
            cellObject.style.color = '#352BFF';
            cellObject.style.textDecoration = 'underline';
        } else if (cell.getField() === 'LEUC_TEST' && formattedValue !== '—' && formattedValue > leucTestThreshold) {
            cellObject.className = cellObject.className + ' pf-u-danger-color-100';
        }

        return formattedValue;
    }

    /**
     * Formats a value to same as the default Tabulator money formatter, except that
     * negative numbers are wrapped in parentheses instead of using the negative sign.
     * Because accountants are dumb.
     * 
     * @see http://tabulator.info/docs/5.0/format for Money formatter params
     * 
     * @param cell            The cell component object
     * @param formatterParams parameters set for the column
     * @param onRendered      function to call when the formatter is rendered. 
     */
    static accountingCurrencyFormatter = (cell: any, formatterParams: any, onRendered: () => void): string => {

        const emptyToSpace = (value) => {
            return value === null || typeof value === "undefined" || value === "" ? "&nbsp;" : value;
        };
        const sanitizeHTML = (value) => {
            if (value) {
                var entityMap = {
                    '&': '&amp;',
                    '<': '&lt;',
                    '>': '&gt;',
                    '"': '&quot;',
                    "'": '&#39;',
                    '/': '&#x2F;',
                    '`': '&#x60;',
                    '=': '&#x3D;'
                };
        
                return String(value).replace(/[&<>"'`=\/]/g, function (s) {
                    return entityMap[s];
                });
            } else {
                return value;
            }
        };
        let floatVal = parseFloat(cell.getValue()),
		    number,
		    integer,
		    decimal,
		    rgx;

		let decimalSym = formatterParams.decimal || ".";
		let thousandSym = formatterParams.thousand || ",";
		let symbol = formatterParams.symbol || "";
		let after = !!formatterParams.symbolAfter;
		let precision = typeof formatterParams.precision !== "undefined" ? formatterParams.precision : 2;

		if (isNaN(floatVal)) {
			return emptyToSpace(sanitizeHTML(cell.getValue()));
		}

		number = precision !== false ? floatVal.toFixed(precision) : floatVal;
		number = String(number).split(".");

		integer = number[0];
		decimal = number.length > 1 ? decimalSym + number[1] : "";

		rgx = /(\d+)(\d{3})/;

		while (rgx.test(integer)) {
			integer = integer.replace(rgx, "$1" + thousandSym + "$2");
		}

        const negativeSign = formatterParams.negativeSign || '-';
		let formattedValue: string =  after ? integer + decimal + symbol : symbol + integer + decimal;

        // If the formatted value isn't negative, we're done
        if (!negativeSign || !formattedValue.includes(negativeSign)) {
            return formattedValue;
        }

        // Remove the negative sign and wrap the value in parentheses
        formattedValue = formattedValue.replace(negativeSign, '');
        return `(${formattedValue})`;
    }

    /**
     * Formats values for the FCP Settlement report which can either be '—' and need to be displayed red,
     * or contain a value which needs to be formatted using the above accountingCurrencyFormatter. This 
     * formatter should be used with the appropriate money formatter params
     * 
     * @see http://tabulator.info/docs/5.0/format for Money formatter params
     * 
     * @param cell            The cell component object
     * @param formatterParams parameters set for the column
     * @param onRendered      function to call when the formatter is rendered. 
     */
     static fcpSettlementEstimateFormatter = (cell: any, formatterParams: any, onRendered: () => void): string => {
        const value = cell.getValue();

        if (value === '—') {
            const cellObject: HTMLElement = cell.getElement();
            cellObject.className = cellObject.className + ' pf-u-danger-color-100';
            return value;
        }

        return TabulatorFormatters.accountingCurrencyFormatter(cell, formatterParams, onRendered);
     }

}